import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FaSyncAlt } from 'react-icons/fa'
import type { ChartData, ChartOptions } from 'chart.js'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import api from '../../../../helpers/apiRequester'
import styles from '../shared/mhlShared.module.scss'
import localStyles from './MilitaryHearingLossWebsiteVisitors.module.scss'
import { SourceTabs, StatTile } from '../shared/SourceTabs'
import type {
    ChaseCampaignResponse,
    VisitorRangeDays,
    VisitorSourceStats,
    VisitorSummary,
    WebformSignupsResponse,
} from '../shared/mhlTypes'
import { referralFilterOptions, referralLabel } from '../shared/mhlFormatting'
import { alpha, colors, CollapsibleSection } from '../../../../ui'
import { ToggleInput } from '../components/ToggleInput/ToggleInput'
import { VisitorsTable } from './VisitorsTable'
import { WebformSignupsTable } from './WebformSignupsTable'
import { ChaseCampaignPanel } from './ChaseCampaignPanel'
import { SourceBreakdownTable } from './SourceBreakdownTable'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

type VisitorTab = 'analytics' | 'chase'
type AnalyticsSection = 'chart' | 'sources' | 'signups' | 'visitors'

const CHART_RANGE_OPTIONS: { value: VisitorRangeDays; label: string }[] = [
    { value: '7', label: 'Last 7 days' },
    { value: '14', label: 'Last 14 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
]

const rangeLabelFor = (days: VisitorRangeDays) => `${days}d`

interface VisitorsByDayResponse {
    success?: boolean
    days?: number
    labels?: string[]
    summary?: VisitorSummary
    chartData?: ChartData<'line'>
    webformSignupsByDay?: number[]
    /** Per-source totals for the whole period; not narrowed by the source filter. */
    bySource?: VisitorSourceStats[]
    message?: string
}

const CHART_OPTIONS: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'bottom',
            labels: { boxWidth: 10, font: { family: 'Poppins', size: 11 }, color: colors.ink3 },
        },
        tooltip: { mode: 'index', intersect: false },
    },
    scales: {
        x: { grid: { display: false }, ticks: { font: { family: 'Poppins', size: 11 }, color: colors.ink4 } },
        y: {
            beginAtZero: true,
            ticks: { precision: 0, font: { family: 'Poppins', size: 11 }, color: colors.ink4 },
            grid: { color: colors.lineSoft },
        },
    },
}

const SIGNUP_COLOUR = '#6b8f71'

const restyle = (data: ChartData<'line'> | undefined): ChartData<'line'> | null => {
    if (!data) return null
    const palette = [colors.accent, colors.ink, SIGNUP_COLOUR]
    return {
        ...data,
        datasets: data.datasets.map((ds, i) => ({
            ...ds,
            borderColor: palette[i % palette.length],
            backgroundColor:
                i === 0 ? alpha(colors.accent, 0.08) : i === 1 ? alpha(colors.ink, 0.06) : alpha(SIGNUP_COLOUR, 0.12),
            pointRadius: 3,
            pointBackgroundColor: palette[i % palette.length],
            borderWidth: i === 2 ? 3 : 2,
            tension: 0.3,
            fill: i === 0 || i === 2,
        })),
    }
}

/**
 * Website visitor analytics for the NIHLC claim site:
 *   Website analytics - headline tiles, then collapsible sections for the
 *                       visitors-vs-signups chart, traffic by referral
 *                       source, webform signups and the per-hit visitor log.
 *                       The source filter narrows all of them except the
 *                       by-source breakdown.
 *   Chase campaign    - drop-off chase enrolment + messages sent
 */
export const MilitaryHearingLossWebsiteVisitors = () => {
    const [tab, setTab] = useState<VisitorTab>('analytics')
    const [chartRange, setChartRange] = useState<VisitorRangeDays>('7')
    // 'all' or a referral source such as 'sentinelforces'.
    const [referral, setReferral] = useState('all')
    // Visitor log starts collapsed: it is the longest section and the tiles
    // already carry the headline visitor numbers.
    const [openSections, setOpenSections] = useState<Record<AnalyticsSection, boolean>>({
        chart: true,
        sources: true,
        signups: true,
        visitors: false,
    })
    const sectionRefs = useRef<Record<AnalyticsSection, HTMLDivElement | null>>({
        chart: null,
        sources: null,
        signups: null,
        visitors: null,
    })
    const [summary, setSummary] = useState<VisitorSummary | null>(null)
    const [bySource, setBySource] = useState<VisitorSourceStats[]>([])
    const [chartRaw, setChartRaw] = useState<ChartData<'line'> | undefined>()
    const [signupsData, setSignupsData] = useState<WebformSignupsResponse | null>(null)
    const [chaseData, setChaseData] = useState<ChaseCampaignResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [reloadKey, setReloadKey] = useState(0)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        const days = Number(chartRange)
        const source = referral === 'all' ? undefined : referral
        const errMsg = (err: any, fallback: string) => err?.response?.data?.message || err?.message || fallback

        // Each section loads independently so one failing endpoint does not
        // blank the others. Signups are narrowed by source client-side below.
        const [visitorsRes, signupsRes, chaseRes] = await Promise.allSettled([
            api({ url: '/military-hearing-loss-visitors-by-day', data: { days, source } }),
            api({ url: '/military-hearing-loss-webform-signups', data: { days } }),
            api({ url: '/military-hearing-loss-chase-campaign', data: { days } }),
        ])
        const problems: string[] = []

        if (visitorsRes.status === 'fulfilled') {
            const body = visitorsRes.value.data as VisitorsByDayResponse
            if (!body?.chartData) {
                problems.push(body?.message || 'No visitor chart data returned')
                setSummary(null)
                setBySource([])
                setChartRaw(undefined)
            } else {
                setSummary(body.summary ?? null)
                setBySource(body.bySource ?? [])
                setChartRaw(body.chartData)
            }
        } else {
            problems.push(`Visitor stats: ${errMsg(visitorsRes.reason, 'failed to load')}`)
            setSummary(null)
            setBySource([])
            setChartRaw(undefined)
        }

        if (signupsRes.status === 'fulfilled' && (signupsRes.value.data as WebformSignupsResponse)?.success) {
            setSignupsData(signupsRes.value.data as WebformSignupsResponse)
        } else {
            const reason = signupsRes.status === 'rejected' ? signupsRes.reason : null
            problems.push(`Website signups: ${errMsg(reason, (signupsRes as any).value?.data?.message || 'failed to load')}`)
            setSignupsData(null)
        }

        if (chaseRes.status === 'fulfilled' && (chaseRes.value.data as ChaseCampaignResponse)?.success) {
            setChaseData(chaseRes.value.data as ChaseCampaignResponse)
        } else {
            const reason = chaseRes.status === 'rejected' ? chaseRes.reason : null
            problems.push(`Chase campaign: ${errMsg(reason, (chaseRes as any).value?.data?.message || 'failed to load')}`)
            setChaseData(null)
        }

        setError(problems.length ? problems.join(' · ') : null)
        setLoading(false)
    }, [chartRange, referral])

    useEffect(() => {
        void load()
    }, [load, reloadKey])

    const refresh = () => setReloadKey((k) => k + 1)

    const setSectionOpen = (section: AnalyticsSection, open: boolean) =>
        setOpenSections((prev) => ({ ...prev, [section]: open }))

    /** Tile click: make sure the section is open, then scroll to it. */
    const jumpTo = (section: AnalyticsSection) => {
        setSectionOpen(section, true)
        // Defer until the section has rendered its body.
        window.setTimeout(() => sectionRefs.current[section]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50)
    }

    const chart = useMemo(() => restyle(chartRaw), [chartRaw])

    const referralOptions = useMemo(() => referralFilterOptions(bySource.map((row) => row.source)), [bySource])

    // The signups endpoint has no source filter, so narrow its rows here.
    const filteredSignups = useMemo<WebformSignupsResponse | null>(() => {
        if (!signupsData || referral === 'all') return signupsData
        return { ...signupsData, signups: signupsData.signups.filter((s) => s.lead_source === referral) }
    }, [signupsData, referral])

    const rangeLabel = rangeLabelFor(chartRange)
    // With a source filter, the fallbacks would be unfiltered, so only the
    // (filtered) visitors-by-day summary is used.
    const fallback = referral === 'all' ? signupsData?.summary : undefined
    const signupsInRange = summary?.webformSignupsInRange ?? fallback?.signupsInRange ?? 0
    const signupsToday = summary?.webformSignupsToday ?? fallback?.signupsToday ?? 0
    const signupsTotal = summary?.webformSignupsTotal ?? fallback?.signupsTotal ?? 0
    const conversion = summary?.conversionRate ?? fallback?.conversionRate ?? 0
    const signupsSummary =
        filteredSignups && referral !== 'all'
            ? {
                  signupsInRange: filteredSignups.signups.length,
                  signed: filteredSignups.signups.filter((s) => s.esign.signed).length,
                  docsComplete: filteredSignups.signups.filter((s) => s.journey_status === 'complete').length,
              }
            : signupsData?.summary
    const referralNote = referral === 'all' ? '' : ` · ${referralLabel(referral)}`

    const tabs = [
        { value: 'analytics' as const, label: 'Website analytics', count: signupsInRange },
        { value: 'chase' as const, label: 'Chase campaign', count: chaseData?.summary.enrolled ?? null },
    ]

    const signupsMeta = signupsSummary
        ? `${signupsSummary.signupsInRange} in ${rangeLabel} · ${signupsSummary.signed} signed · ${signupsSummary.docsComplete} complete${referralNote}`
        : null

    const visitorsMeta = summary
        ? `${summary.pageviewsInRange} page loads · ${summary.uniqueInRange} unique IPs in ${rangeLabel}${referralNote}`
        : null

    const sourcesMeta = bySource.length ? `${bySource.length} source${bySource.length === 1 ? '' : 's'} · ${rangeLabel}` : null

    return (
        <div className={styles.wrap}>
            {error && <div className={styles.errorBanner}>{error}</div>}

            <div className={localStyles.controlsRow}>
                <SourceTabs value={tab} options={tabs} onChange={setTab} ariaLabel="Website visitor sections" />
                <div className={localStyles.controlsRight}>
                    {tab === 'analytics' && (
                        <select
                            className={styles.select}
                            value={referral}
                            onChange={(e) => setReferral(e.target.value)}
                            aria-label="Referral source"
                        >
                            {referralOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    )}
                    <ToggleInput value={chartRange} options={CHART_RANGE_OPTIONS} onChange={setChartRange} ariaLabel="Period" fit />
                    <button type="button" className={styles.btn} onClick={refresh} disabled={loading}>
                        <FaSyncAlt size={11} /> Refresh
                    </button>
                </div>
            </div>

            {tab === 'analytics' && (
                <>
                    {loading && !summary && !signupsData ? (
                        <div className={styles.emptyState}>Loading…</div>
                    ) : (
                        <>
                            <div className={`${localStyles.statsRowWide} ${styles.statStrip}`}>
                                <StatTile
                                    label={`Webform signups (${rangeLabel})`}
                                    value={signupsInRange}
                                    accent
                                    note="Clients who signed up through the website"
                                    onClick={() => jumpTo('signups')}
                                />
                                <StatTile
                                    label="Webform signups today"
                                    value={signupsToday}
                                    tone={signupsToday > 0 ? 'good' : undefined}
                                    note="Since midnight"
                                    onClick={() => jumpTo('signups')}
                                />
                                <StatTile
                                    label="Webform signups (all time)"
                                    value={signupsTotal}
                                    note="Every public claim-form submission"
                                    onClick={() => jumpTo('signups')}
                                />
                                <StatTile
                                    label={`Unique visitors (${rangeLabel})`}
                                    value={summary?.uniqueInRange ?? 0}
                                    note={`${summary?.today ?? 0} unique today`}
                                    onClick={() => jumpTo('visitors')}
                                />
                                <StatTile
                                    label={`Page loads (${rangeLabel})`}
                                    value={summary?.pageviewsInRange ?? 0}
                                    note="Every open or refresh of the form"
                                    onClick={() => jumpTo('visitors')}
                                />
                                <StatTile
                                    label={`Conversion (${rangeLabel})`}
                                    value={`${conversion}%`}
                                    tone={conversion >= 5 ? 'good' : conversion >= 2 ? 'warn' : undefined}
                                    note="Signups as a share of unique visitors"
                                />
                            </div>

                            <div className={localStyles.conversionBar}>
                                <span>
                                    <strong>{summary?.uniqueInRange ?? 0}</strong> unique visitors
                                </span>
                                <div className={localStyles.conversionTrack} aria-hidden="true">
                                    <div className={localStyles.conversionFill} style={{ width: `${Math.min(100, conversion)}%` }} />
                                </div>
                                <span>
                                    <strong>{signupsInRange}</strong> signed up ({conversion}%)
                                </span>
                                {chaseData && (
                                    <button type="button" className={localStyles.inlineLink} onClick={() => setTab('chase')}>
                                        · <strong>{chaseData.summary.enrolled}</strong> in chase
                                    </button>
                                )}
                            </div>

                            <div className={localStyles.sections}>
                                <div ref={(el) => (sectionRefs.current.chart = el)}>
                                    <CollapsibleSection
                                        variant="group"
                                        title="Visitors vs signups"
                                        meta={`by day · ${rangeLabel}${referralNote}`}
                                        open={openSections.chart}
                                        onToggle={(open) => setSectionOpen('chart', open)}
                                    >
                                        {chart ? (
                                            <div className={localStyles.chartHost}>
                                                <Line options={CHART_OPTIONS} data={chart} />
                                            </div>
                                        ) : (
                                            <p className={styles.emptyStateSmall}>No chart data.</p>
                                        )}
                                    </CollapsibleSection>
                                </div>

                                <div ref={(el) => (sectionRefs.current.sources = el)}>
                                    <CollapsibleSection
                                        variant="group"
                                        title="Traffic by source"
                                        meta={sourcesMeta}
                                        open={openSections.sources}
                                        onToggle={(open) => setSectionOpen('sources', open)}
                                    >
                                        <SourceBreakdownTable
                                            rows={bySource}
                                            loading={loading}
                                            selected={referral}
                                            onSelect={setReferral}
                                        />
                                    </CollapsibleSection>
                                </div>

                                <div ref={(el) => (sectionRefs.current.signups = el)}>
                                    <CollapsibleSection
                                        variant="group"
                                        title="Website signups"
                                        meta={signupsMeta}
                                        open={openSections.signups}
                                        onToggle={(open) => setSectionOpen('signups', open)}
                                    >
                                        <WebformSignupsTable data={filteredSignups} loading={loading} />
                                    </CollapsibleSection>
                                </div>

                                <div ref={(el) => (sectionRefs.current.visitors = el)}>
                                    <CollapsibleSection
                                        variant="group"
                                        title="Visitor log"
                                        meta={visitorsMeta}
                                        open={openSections.visitors}
                                        onToggle={(open) => setSectionOpen('visitors', open)}
                                    >
                                        <VisitorsTable rangeDays={chartRange} source={referral} reloadKey={reloadKey} />
                                    </CollapsibleSection>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}

            {tab === 'chase' && <ChaseCampaignPanel data={chaseData} loading={loading} rangeLabel={rangeLabel} onChanged={refresh} />}
        </div>
    )
}
